import { yupResolver } from '@hookform/resolvers/yup';
import { Badge, Box, Button, Grid, Step, StepLabel, Stepper } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType } from 'yup';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { updateEvent } from 'src/shared/services/eventService';
import { toast } from 'react-toastify';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import dayjs from 'dayjs';
import { MultiStepIcon } from 'src/shared/components/multistepper/multiStepIcon';
import { StyledMultiStepConnector } from 'src/shared/components/multistepper/styledMultiStepConnector';
import { useMultiStepForm } from 'src/shared/hooks/useMultiStepForm';
import { ArrowBackIos, ArrowForwardIos, PriorityHigh, Check } from '@mui/icons-material';
import { useEventContext } from 'src/shared/contexts/eventContext';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { Event } from '../models/event';
import { EventPutRequest } from '../models/eventRequest';
import { EventFormStepEventType } from './eventFormStepEventType';
import { EventFormStepDescription } from './eventFormStepDescription';
import { EventFormStepDateAndPlace } from './eventFormStepDateAndPlace';
import { EventFormStepParticipants } from './eventFormStepParticipants';
import { EventFormStepContact } from './eventFormStepContact';
import { useSchemaEvent } from '../schemas/useSchemaEvent';

type EventEditModalProps = {
  modalOpen: boolean;
  event: Event;
  onSave: () => void;
  onClose: () => void;
  callback: () => void;
};

export const EventEditModal = (props: EventEditModalProps) => {
  const { modalOpen, onSave, onClose, event } = props;
  const { currentUser } = useGlobals();
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading] = useState(false);
  const [serverErrors] = useState<string[]>([]);
  const { uploadedImageFile, selectedEventType } = useEventContext();

  const modalTop = useRef<HTMLDivElement>(null);

  const eventRequestPostSchema = useSchemaEvent();

  type EventRequestPostInterface = InferType<typeof eventRequestPostSchema>;

  const formMethods = useForm<EventRequestPostInterface>({
    resolver: yupResolver(eventRequestPostSchema),
    defaultValues: {
      name: event?.name ?? '',
      description: event?.description ?? '',
      date: event?.date ? dayjs(event?.date).toDate() : undefined,
      fee: event?.fee ?? undefined,
      memberFee: event?.memberFee ?? undefined,
      minParticipants: event?.minParticipants ?? undefined,
      maxParticipants: event?.maxParticipants ?? undefined,
      address: event?.address ?? '',
      postcode: event?.postcode ?? '',
      city: event?.city ?? '',
      lastDateForApplication: event?.lastDateForApplication ? dayjs(event?.lastDateForApplication).toDate() : undefined,
      contactPersonName: event?.contactPersonName ?? '',
      contactPersonEmail: event?.contactPersonEmail ?? '',
      contactPersonPhone: event?.contactPersonPhone ?? '',
      allergy: event?.allergy,
      publicSignUp: event?.publicSignUp,
      membersOnly: event?.membersOnly,
      association: event?.association ?? undefined,
      district: event?.district ?? undefined,
      files: [],
      owner_id: event?.owner_id ?? 0,
      useTickets: event.useTickets,
      locationName: event.locationName ?? '',
      tickets:
        event.tickets.length > 0
          ? event.tickets
          : [
              {
                ticketName: '',
                price: 0,
                memberPrice: 0,
                invoiceArticleId: undefined,
                createdById: currentUser?.user_id || 0,
                enabledAt: DateHelper.getDateTime(new Date()),
              },
            ],
    },
  });

  const { handleSubmit, watch } = formMethods;

  const onSubmit = useCallback(
    async (data: EventRequestPostInterface) => {
      ConsoleHelper.log('onSubmit', data);

      const setTicketData = (data.tickets ?? []).map((ticket) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { deleteableWhileEdit, id, ticketId, ...rest } = ticket as any;

        const parsedId = [id, ticketId].map((v) => Number(v)).find((n) => Number.isFinite(n) && n > 0) ?? 0;

        return {
          ...rest,
          id: parsedId,
          price: Number(rest.price) || 0,
          memberPrice: Number(rest.memberPrice) || 0,
        };
      });

      const dataToPost: EventPutRequest = {
        ...data,
        id: event?.id ?? 0,
        date: DateHelper.postDateTime(data.date),
        lastDateForApplication: DateHelper.postDateTime(data.lastDateForApplication),
        localAss_id: (data.association?.localAss_id !== undefined && data.association?.localAss_id) || 0,
        countyAss_id: (data.district?.countyAss_id !== undefined && data.district?.countyAss_id) || 0,
        files: uploadedImageFile ? [uploadedImageFile] : [],
        tickets: setTicketData,
      };

      ConsoleHelper.log('dataToPost: ', dataToPost);

      try {
        await updateEvent(dataToPost);
        if (isMounted()) toast.success(<>{t('event:saveSuccess')}</>);
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error', e);
      } finally {
        if (isMounted()) onSave();
      }
    },
    [event?.id, isMounted, onSave, t, uploadedImageFile],
  );

  const onHandleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const onHandleSave = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const { step, steps, currentStepIndex, isFirstStep, isLastStep, back, next, goTo } = useMultiStepForm([
    <EventFormStepEventType />,
    <EventFormStepDescription />,
    <EventFormStepDateAndPlace />,
    <EventFormStepParticipants editMode eventId={event.id} />,
    <EventFormStepContact />,
  ]);

  // Ordningen ska vara samma som i useMultiStepForm
  const useTickets = !!watch('useTickets');
  const ownerId = watch('owner_id');
  const fee = watch('fee');
  const memberFee = watch('memberFee');
  const eventTickets = watch('tickets') || [];

  const isEmpty = (v: unknown) => v === '' || v === null || v === undefined;

  const noParticipants =
    (isEmpty(watch('minParticipants')) && watch('minParticipants') !== 0) || (isEmpty(watch('maxParticipants')) && watch('maxParticipants') !== 0);
  const ownerMissing = !ownerId;
  const feeInvalidWhenFlat = !useTickets && (isEmpty(fee) || isEmpty(memberFee));
  const invalidTicketsWhenTicketMode =
    useTickets && (!eventTickets.length || eventTickets.some((ticket) => !ticket?.ticketName || ticket.ticketName.trim() === ''));

  const stepFourInvalid = noParticipants || ownerMissing || feeInvalidWhenFlat || invalidTicketsWhenTicketMode;
  const stepOneInvalid = false;
  const stepTwoInvalid = !watch('name');
  const stepThreeInvalid = !watch('date') || !watch('lastDateForApplication') || !watch('address') || !watch('postcode') || !watch('city');

  const isStepComplete = [!stepOneInvalid, !stepTwoInvalid, !stepThreeInvalid, !stepFourInvalid, true];

  const allStepsComplete = isStepComplete.every((v) => v === null || v);

  return (
    <GenericDialog
      open={modalOpen}
      disableBackdropClosing
      disableEnforceFocus
      padding={0}
      dialogTitle={t('event:editEvent')}
      dialogSubTitle={t(`event:eventType.${selectedEventType}`)}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogActions={
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {!isFirstStep ? (
            <Button type='button' variant='outlined' onClick={back} startIcon={<ArrowBackIos fontSize='small' />}>
              {t('common:button.back')}
            </Button>
          ) : (
            <Box />
          )}
          <Box sx={{ pr: 1 }}>
            {!isLastStep && (
              <Button type='button' variant='contained' sx={{ mr: 1 }} disableElevation onClick={next} endIcon={<ArrowForwardIos fontSize='small' />}>
                {t('common:button.continue')}
              </Button>
            )}
            <Badge
              sx={{
                '& .MuiBadge-badge': {
                  borderRadius: '100%',
                  height: '25px',
                },
                '& .badgeicon': {
                  fontSize: '0.8rem',
                },
              }}
              color={allStepsComplete ? 'success' : 'warning'}
              badgeContent={!allStepsComplete ? <PriorityHigh className='badgeicon' /> : <Check className='badgeicon' />}
            >
              <Button
                type='submit'
                disableElevation
                variant={isLastStep || allStepsComplete ? 'contained' : 'outlined'}
                disabled={!allStepsComplete}
                onClick={onHandleSave}
              >
                {t('common:button.saveClose')}
              </Button>
            </Badge>
          </Box>
        </Box>
      }
      dialogContent={
        <Box
          ref={modalTop}
          sx={{ position: 'relative', border: (theme) => (serverErrors.length > 0 ? `8px solid ${theme.palette.error.main}` : 'none') }}
        >
          <LoadingOverlay loading={loading} />
          <Stepper activeStep={currentStepIndex} alternativeLabel sx={{ mb: 2, mt: 3 }} connector={<StyledMultiStepConnector />}>
            {steps.map((_item, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Step key={i} onClick={() => goTo(i)}>
                <StepLabel
                  StepIconComponent={(stepProps) => <MultiStepIcon {...stepProps} isDisabled={false} isComplete={isStepComplete[i] || false} />}
                >
                  {i + 1}. {t(`event:stepLabels.step${i}`)}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box sx={{ p: 2 }}>
            <GridWrapper>
              <Grid item xs={12} mt={1.5}>
                <FormProvider {...formMethods}>
                  <form onSubmit={handleSubmit(onSubmit)}>{step}</form>
                </FormProvider>
              </Grid>
            </GridWrapper>
          </Box>
        </Box>
      }
    />
  );
};
